import { useRef, useState } from 'react';
import { calculateShiftSelection } from './selectionUtils';

interface UseMultiSelectArgs<T> {
  allowShiftSelect: boolean;
  getKey: (item: T) => string | null;
}

/**
 * Backspace on an empty search field removes the most-recently-added pill —
 * matches the react-select behavior callers expect
 */
export const handleBackspaceRemoval = <T extends { value: string | null }>(
  e: React.KeyboardEvent<HTMLInputElement>,
  inputValue: string,
  currentSelection: T[],
  onRemove: (val: T['value']) => void
) => {
  if (
    e.key === 'Backspace' &&
    inputValue === '' &&
    currentSelection.length > 0
  ) {
    e.preventDefault();
    onRemove(currentSelection[currentSelection.length - 1].value);
  }
};

interface HandleClickArgs<T> {
  val: string;
  visibleOptions: T[];
  currentSelection: T[];
}

/**
 * Owns the local state needed to drive multi-select with optional shift-range:
 * the last-clicked anchor index and the shift-key captured on mousedown.
 *
 * Returns a `captureShiftKey` handler to attach to the options container
 * (because Mantine's onOptionSubmit doesn't pass the click event), and a
 * `handleClick` that resolves a click into the next selection — or `null`
 * if the clicked value can't be found in either list.
 */
export const useMultiSelect = <T>({
  allowShiftSelect,
  getKey,
}: UseMultiSelectArgs<T>) => {
  const [lastSelectedIndex, setLastSelectedIndex] = useState(-1);
  const shiftKeyRef = useRef(false);

  const captureShiftKey = (e: React.MouseEvent) => {
    if (allowShiftSelect) shiftKeyRef.current = e.shiftKey;
  };

  const handleClick = ({
    val,
    visibleOptions,
    currentSelection,
  }: HandleClickArgs<T>): T[] | null => {
    const isShift = shiftKeyRef.current;
    shiftKeyRef.current = false;

    const clickedOption = visibleOptions.find((o) => getKey(o) === val);
    if (!clickedOption) return null;

    const isShiftRange = isShift && allowShiftSelect && lastSelectedIndex >= 0;

    if (isShiftRange) {
      const { newSelection, newLastSelectedIndex } = calculateShiftSelection(
        clickedOption,
        visibleOptions,
        currentSelection,
        lastSelectedIndex,
        getKey
      );
      setLastSelectedIndex(newLastSelectedIndex);
      return newSelection;
    }

    setLastSelectedIndex(visibleOptions.indexOf(clickedOption));
    const alreadySelected = currentSelection.some((s) => getKey(s) === val);
    return alreadySelected
      ? currentSelection.filter((s) => getKey(s) !== val)
      : [...currentSelection, clickedOption];
  };

  return { captureShiftKey, handleClick };
};
